import { Chats } from "../../features/chat/domain/entities/getChat";
import Category from "../../features/profile/domain/entities/category";
import GetProfileConfigInfo from "../../features/profile/domain/entities/getProfileConfigInfo";
import User from "../../features/profile/domain/entities/getUser";
import { ProfileInfo } from "../../features/profile/presentation/interfaces/EditProfileInterface";
import Compressor from 'compressorjs';

export class AppConstants {
	static async getRightOrLeftSide<T>(execute: Promise<T>): Promise<T> {
		try {
			const result = await execute;
			return result;
		} catch (error) {
			if ((error as any)?.preserveUserMessage && (error as Error).message) {
				throw new Error((error as Error).message);
			}
			throw new Error(
				(error as any)?.response?.status?.toString?.() ?? "Something went wrong"
			);
		}
	}
}

export const validateRUT = (rut: string) => {
	rut = rut.replace(/\./g, "").replace(/-/g, "");

	if (rut.length < 2) {
		return false;
	}

	const num = rut.slice(0, -1);
	const verifier = rut.slice(-1).toUpperCase();

	let sum = 0;
	let multiplier = 2;

	for (let i = num.length - 1; i >= 0; i--) {
		sum += parseInt(num.charAt(i), 10) * multiplier;
		multiplier = multiplier === 7 ? 2 : multiplier + 1;
	}

	const dv = 11 - (sum % 11);
	const expectedVerifier = dv === 11 ? "0" : dv === 10 ? "K" : dv.toString();

	return expectedVerifier === verifier;
};

export const formatRut = (rut: string) => {
	// Eliminar cualquier carácter que no sea número o K/k
	const cleanedValue = rut.replace(/[^\dKk]/g, "");

	if (cleanedValue.length <= 1) {
		return cleanedValue;
	}

	// Separar el dígito verificador del resto del RUT
	const body = cleanedValue.slice(0, -1);
	const verifier = cleanedValue.slice(-1).toUpperCase();

	// Formatear el cuerpo del RUT con puntos
	const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

	// Combinar el cuerpo formateado y el dígito verificador
	return `${formattedBody}-${verifier}`;
};
interface AgeRangeItem {
	key: string;
	label: string;
}
export const AgeRange: AgeRangeItem[] = Array.from({ length: 83 }, (_, i) => ({
	key: (i + 18).toString(),
	label: (i + 18).toString(),
}));

export const calculateDesires = (
	getProfileConfig: GetProfileConfigInfo,
	desiresList: Category[]
) => {
	return getProfileConfig?.user_has_desire
		.map((desire) => desiresList.find((d) => d.id === desire.desire_id)?.name)
		.filter((name): name is string => name !== undefined);
};

export const calculateInterest = (getProfileConfig: GetProfileConfigInfo) => {
	return getProfileConfig?.user_has_interest?.map(
		(interest) => interest.interest
	);
};

export const calculateBirth = (user: User) => {
	if (!user?.birth) return undefined;
	const birthDate = new Date(user.birth);
	const today = new Date();
	let age = today.getFullYear() - birthDate.getFullYear();
	const hasBirthdayPassed =
		today.getMonth() > birthDate.getMonth() ||
		(today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
	if (!hasBirthdayPassed) age--;
	return age;
};

export const calculateSexualOrientation = (
	user: User,
	sexualOrientationList: Category[]
) => {
	return user?.sexual_orientation_id
		? sexualOrientationList.find(
				(orientation) => orientation.id === user.sexual_orientation_id
		  )?.name
		: undefined;
};

export const calculateMaritalStatus = (
	user: User,
	maritialList: Category[]
) => {
	return user?.marital_status_id
		? maritialList.find((status) => status.id === user.marital_status_id)?.name
		: undefined;
};

export const calculateGender = (user: User, genderList: Category[]) => {
	return user?.gender_id
		? genderList.find((g) => g.id === user.gender_id)?.name
		: undefined;
};

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const getRelativeDate = (hourString: string): { relativeText: string; isExactTime: boolean } => {
	const date = new Date(hourString);
	const localDateString = date.toLocaleDateString("en-GB", { timeZone: userTimeZone });
	const now = new Date();
	const todayString = now.toLocaleDateString("en-GB", { timeZone: userTimeZone });
	if (localDateString === todayString) return { relativeText: "Today", isExactTime: true };
	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	const yesterdayString = yesterday.toLocaleDateString("en-GB", { timeZone: userTimeZone });
	if (localDateString === yesterdayString) return { relativeText: "Yesterday", isExactTime: false };
	const diffInDays = Math.ceil((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
	if (diffInDays > 0 && diffInDays < 7) return { relativeText: `${diffInDays} days ago`, isExactTime: false };
	return { relativeText: `${calculateDate(date)}`, isExactTime: false };
};

export const formatDate = (hourString: string, showYear?: boolean): string => {
	const { relativeText, isExactTime } = getRelativeDate(hourString);
	return isExactTime && !showYear ? formatHour(hourString) : relativeText;
};

export const formatDateLastLogged = (hourString: string): string => {
	const { relativeText } = getRelativeDate(hourString);
	return `Last logged in ${relativeText.toLowerCase()}`;
};

export const formatHour = (hourString: string): string => {
	const date = new Date(hourString);
	return date.toLocaleTimeString("en-GB", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: userTimeZone,
	});
};

export const calculateDate = (date: Date): string => {
	return date.toLocaleDateString("en-GB", {
		year: "numeric",
		month: "numeric",
		day: "numeric",
		timeZone: userTimeZone,
	});
};

export const sortPhotos = (photos: ProfileInfo[]) => {
	return photos
		.map((photo, index) => ({ photo, index }))
		.sort((a, b) => {
			const aIsProfile = a.photo.is_profile === true;
			const bIsProfile = b.photo.is_profile === true;
			if (aIsProfile !== bIsProfile) return aIsProfile ? -1 : 1;

			const aIsPublic = a.photo.is_public !== false;
			const bIsPublic = b.photo.is_public !== false;
			if (aIsPublic !== bIsPublic) return aIsPublic ? -1 : 1;

			return a.index - b.index;
		})
		.map(({ photo }) => photo);
}

type ValidateFieldOptions = {
	field?: string;
	max?: number;
};

type ValidateFieldResult = {
	isValid: boolean;
	error: string | null;
};

export const validateField = (
	text: string,
	options?: ValidateFieldOptions
): ValidateFieldResult => {
	const {
		field = "Description",
		max = 1000,
	} = options ?? {};

	if (text.length === 0) {
		return {
			isValid: field === "Description",
			error: null,
		};
	}

	if (text.length > max) {
		return {
			isValid: false,
			error: `${field} cannot exceed ${max} characters`,
		};
	}

	const blockedReason = getBlockedTextWarning(text, field);
	if (blockedReason) {
		return {
			isValid: false,
			error: blockedReason,
		};
	}

	return {
		isValid: true,
		error: null,
	};
};

export const getBlockedTextWarning = (
	text: string,
	field = "Description"
): string | null => {
	const normalizedText = text.trim();
	if (!normalizedText) return null;

	const blockedWords = [
		"WhatsApp",
		"Telegram",
		"Snapchat",
		"Instagram",
		"Insta",
		"OnlyFans",
		"Kik",
		"Signal",
		"Discord",
		"WeChat",
		"Line",
		"Zangi",
		"Cash App",
		"Venmo",
		"Zelle",
		"bitcoin",
		"btc",
		"crypto",
		"usdt",
		"forex",
		"investment",
		"gift card",
		"sugar daddy",
	];

	const blockedPhrases = [
		"text me",
		"message me on",
		"add me on",
		"reach me on",
		"contact me on",
		"talk to me on",
		"hit me up on",
	];

	const emailPattern = /\b[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/i;
	if (emailPattern.test(normalizedText)) {
		return `${field} cannot contain email addresses`;
	}

	const phonePattern = /(?:\+?\d[\d\s().-]{6,}\d)/;
	if (phonePattern.test(normalizedText)) {
		return `${field} cannot contain phone numbers`;
	}

	const linkPatterns = [
		/(https?:\/\/|www\.)\S+/i,
		/\b[\w-]+(\.[\w-]+)+([/?#][^\s]*)?\b/i,
	];

	for (const pattern of linkPatterns) {
		if (pattern.test(normalizedText)) {
			return `${field} cannot contain links`;
		}
	}

	for (const phrase of blockedPhrases) {
		const phrasePattern = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, "i");
		if (phrasePattern.test(normalizedText)) {
			return `${field} cannot contain '${phrase}'`;
		}
	}

	for (const word of blockedWords) {
		const wordPattern = new RegExp(`\\b${escapeRegExp(word)}\\b`, "i");
		if (wordPattern.test(normalizedText)) {
			return `${field} cannot contain '${word}'`;
		}
	}

	return null;
};

export const sanitizeAboutText = (rawText?: string | null): string => {
	if (!rawText || !rawText.trim()) return "";

	let text = rawText;
	const patterns = [
		/\b[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/gi,
		/(https?:\/\/|www\.)\S+/gi,
		/\b[\w-]+(\.[\w-]+)+([/?#][^\s]*)?\b/gi,
	];

	for (const pattern of patterns) {
		text = text.replace(pattern, " ");
	}

	return text.replace(/\s+/g, " ").trim();
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const cachedImages: { url: string; img: HTMLImageElement }[] = [];
export const clearAllCachedImagesExcept = (urlsToKeep: string[]) => {
	const urlSet = new Set(urlsToKeep);
	for (let i = cachedImages.length - 1; i >= 0; i--) {
		const cached = cachedImages[i];
		if (!urlSet.has(cached.url)) {
			cached.img.src = "";
			cachedImages.splice(i, 1);
		}
	}
};

export const precacheImages = (imageUrls: string[]) => {
	imageUrls.forEach(url => {
		const img = new Image();
		img.src = url;
		cachedImages.push({ url, img });
	});
};

export const determineCurrentStatus = (selectedChat: Chats) => {
	const { status_user_id1, status_user_id2, status } = selectedChat;
	if (status_user_id1 === "leaved" || status_user_id2 === "leaved") return "leaved";
	if (status_user_id1 === "blocked" || status_user_id2 === "blocked") return "blocked";
	if (status_user_id1 === "deleted" || status_user_id2 === "deleted") return "deleted";
	if (status === "banned" || status === "inactive") return status;
	if (status_user_id1 === "active" && status_user_id2 === "active") return "active";
	return "matched";
};

type CompressImageOptions = {
	quality?: number;
	maxWidth?: number;
	maxHeight?: number;
}

export const compressImage = (file: File, options?: CompressImageOptions): Promise<File> => {
	const {
		quality = 0.7,
		maxWidth = 1280,
		maxHeight = 1280,
	} = options ?? {};

	return new Promise((resolve, reject) => {
		new Compressor(file, {
			quality,
			maxWidth,
			maxHeight,
			mimeType: 'image/jpeg',
			convertTypes: ['image/png', 'image/webp', 'image/heic', 'image/heif'],
			convertSize: 0,
			success(result) {
				const compressedFile = new File([result], file.name.replace(/\.png$/, '.jpg'), { type: 'image/jpeg' });
				resolve(compressedFile);
			},
			error(err) {
				reject(err);
			}
		});
	});
};
export const isMatchMedia = window.matchMedia("(display-mode: standalone)").matches
